<script setup lang="ts">
/**
 * PayHeroMessage — texte en citation de l'en-tête de la page /pay/[slug] : le
 * message personnalisé du QR code, ou à défaut la description du lien de
 * paiement (cf. PayHero). Replié avec « Voir plus » (TexteRepliable).
 *
 * Rendu comme une CITATION — italique — et non comme un sous-titre : c'est un
 * mot adressé au donateur, pas une ligne d'interface. Un message COURT reste
 * centré et nu ; un message long passe à gauche avec le guillemet. Sur une
 * seule ligne brève, le guillemet est plus imposant que la phrase et
 * l'alignement à gauche laisse un grand vide à droite — « Merci ❤️ » n'a pas
 * besoin d'être mis en scène. Au-delà, le texte se lit mal centré : les bords
 * partent en dents de scie (Karim 2026-08-26).
 */

interface Props {
  message: string;
  /** `banner` : blanc sur la bannière assombrie ; `plain` : gris sur fond blanc. */
  tone: "banner" | "plain";
  /** Message court sans bannière : à gauche sur desktop, centré sur mobile. */
  isDesktop: boolean;
}

const props = defineProps<Props>();

const messageLong = computed(
  () => props.message.includes("\n") || props.message.length > 60,
);

const classeConteneur = computed(() => {
  if (props.tone === "banner") {
    return messageLong.value
      ? "relative w-full pl-9 text-left text-white/80"
      : "text-center text-white/80";
  }
  if (messageLong.value) return "relative mt-7 pl-9 text-left text-gray-500";
  return props.isDesktop
    ? "mt-5 text-left text-gray-500"
    : "mt-3 text-center text-gray-500";
});
</script>

<template>
  <!-- Le guillemet est HORS du texte replié : `line-clamp` pose
       `overflow: hidden`, qui le couperait. -->
  <blockquote
    :class="['text-sm italic leading-relaxed', classeConteneur]"
  >
    <span
      v-if="messageLong"
      :class="[
        'absolute left-0 -top-4 font-serif text-[52px] leading-none not-italic',
        tone === 'banner'
          ? 'text-white opacity-40'
          : 'text-[var(--color-main)] opacity-30',
      ]"
      aria-hidden="true"
    >&ldquo;</span>
    <!-- La bannière garde le montant en vedette : 3 lignes ; sans bannière : 4. -->
    <TexteRepliable
      :texte="message"
      :lignes="tone === 'banner' ? 3 : 4"
      :couleur-bouton="tone === 'banner' ? 'blanc' : 'noir'"
    />
  </blockquote>
</template>
